import React, { useRef, useEffect } from 'react';
import IconRenderer from '../../ui/IconRenderer';
import { ReactComponent as LearnModeIcon } from '../../../Icons/learn-mode.svg';
import { ReactComponent as BookIcon } from '../../../Icons/book.svg';
import ExploreCard from './ExploreCard';
import CourseGenerationLoading from './CourseGenerationLoading';
import ExploreFront from './ExploreFront';
import ExploreGrid from './ExploreGrid';
import ExploreBackInfo from './ExploreBackInfo';
import ExplorePreview from './ExplorePreview';
import { useCourseGenerator } from '../../../hooks/useCourseGenerator';
import './CourseGenerator.css';

const PROGRAMMING_LANGUAGES = [
  'JavaScript',
  'Python',
  'Java',
  'C++',
  'C#',
  'Ruby',
  'PHP',
  'Swift',
  'Go',
  'Rust',
  'TypeScript',
  'SQL',
  'HTML/CSS',
  'Shell',
  'Other'
];

const CourseGenerator = ({ onGenerate, onGenerateCookbookOnly, tokenBalance: propTokenBalance, onTokenBalanceUpdate }) => {
  // Ref for scrolling the explore grid
  const exploreGridRef = useRef(null);

  // Use the custom hook for all business logic and state management
  const {
    // State
    language,
    setLanguage,
    availableLanguages,
    tokenBalance,
    insufficientTokensError,
    isGenerating,
    generationProgress,
    currentGenerationStep,
    currentView,
    setCurrentView,
    exploreInfoMode,
    setExploreInfoMode,
    exploreFilter,
    setExploreFilter,
    searchQuery,
    setSearchQuery,
    filterOwned,
    setFilterOwned,
    currentCardIndex,
    selectedTemplate,
    showLanguageDropdown,
    setShowLanguageDropdown,
    showLanguagesInGrid,
    setShowLanguagesInGrid,
    showTypeDropdown,
    setShowTypeDropdown,
    showTypesInGrid,
    setShowTypesInGrid,
    showPurchaseHistory,
    setShowPurchaseHistory,
    purchases,
    purchaseFilter,
    setPurchaseFilter,
    purchaseTypeFilter,
    setPurchaseTypeFilter,
    isLoadingPurchases,
    restoringPurchaseId,

    // Refs
    languageDropdownRef,
    categoryDropdownRef,
    typeDropdownRef,
    exploreSectionRef,
    createCourseSectionRef,

    // Templates and Catalog
    templateCourses,
    templateCookbooks,
    catalogItems,
    isLoadingCatalog,
    filteredTemplates,
    newlyAddedItems,
    isLoadingNewItems,

    // Category
    selectedCategory,
    setSelectedCategory,
    showCategoryDropdown,
    setShowCategoryDropdown,
    showCategoriesInGrid,
    setShowCategoriesInGrid,
    handleCategorySelect,
    handleTypeSelect,
    availableCategories,
    getCategoryDisplayName,

    // Handlers
    handleExampleClick,
    handleViewDetails,
    handleCardClick,
    handleNextCard,
    handlePreviousCard,
    handleShuffle,
    handleBackToFront,
    handleCreateCourse,
    handleBuyTemplate,
    handleGenerateFromForm,
    handleLanguageSelect,
    handleRestorePurchase,
    scrollToExplore,
    scrollToCreateCourse,
    formatDate,
    isTemplateOwned,
    getTemplateOwnershipStatus
  } = useCourseGenerator({
    onGenerate,
    onGenerateCookbookOnly,
    tokenBalance: propTokenBalance,
    onTokenBalanceUpdate
  });



  return (
    <>
      {/* Purchase History Section - Show at top when active */}
      {showPurchaseHistory && (
        <div className="purchase-history-section">
          <div className="purchase-history-header">
            <div className="purchase-history-header-content">
              <h3 className="purchase-history-title">Owned</h3>
              <p className="purchase-history-subtitle">View and restore your owned courses and cookbooks</p>
            </div>
            <button
              type="button"
              className="purchase-history-close-btn"
              onClick={() => setShowPurchaseHistory(false)}
              aria-label="Close purchase history"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Purchase Filters */}
          <div className="purchase-filters">
            <div className="purchase-filter-group">
              <label className="purchase-filter-label">Status:</label>
              <div className="purchase-filter-buttons">
                {['all', 'active', 'deleted', 'restored'].map(status => (
                  <button
                    key={status}
                    type="button"
                    className={`purchase-filter-btn ${purchaseFilter === status ? 'active' : ''}`}
                    onClick={() => setPurchaseFilter(status)}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="purchase-filter-group">
              <label className="purchase-filter-label">Type:</label>
              <div className="purchase-filter-buttons">
                {['all', 'course', 'cookbook'].map(type => (
                  <button
                    key={type}
                    type="button"
                    className={`purchase-filter-btn ${purchaseTypeFilter === type ? 'active' : ''}`}
                    onClick={() => setPurchaseTypeFilter(type)}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Purchase List */}
          {isLoadingPurchases ? (
            <div className="purchase-history-loading">
              <div className="spinner"></div>
              <p>Loading purchase history...</p>
            </div>
          ) : purchases.length === 0 ? (
            <div className="purchase-history-empty">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
              <p>No purchases found</p>
              <p className="purchase-history-empty-subtitle">Owned content will appear here after you generate courses or cookbooks</p>
            </div>
          ) : (
            <div className="explore-grid-wrapper">
              <div className="explore-grid">
                {purchases.map((purchase) => {
                  const metadata = purchase.metadata || {};
                  const status = purchase.status || 'active';
                  const contentType = purchase.contentType || 'course';
                  const purchaseLanguage = metadata.language || 'JavaScript';

                  // Status indicator for right column
                  let rightContent;
                  if (status === 'active') {
                    rightContent = (
                      <div className="explore-grid-card-owned-indicator" title="Active">
                        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </div>
                    );
                  } else if (status === 'restored') {
                    rightContent = (
                      <div className="explore-grid-card-owned-indicator" title="Restored" style={{ borderColor: 'rgba(33, 150, 243, 0.5)', color: 'rgba(33, 150, 243, 0.9)' }}>
                        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                          <path d="M21 3v5h-5" />
                          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                          <path d="M3 21v-5h5" />
                        </svg>
                      </div>
                    );
                  } else if (status === 'deleted') {
                    rightContent = (
                      <div className="explore-grid-card-owned-indicator" title="Deleted" style={{ borderColor: 'rgba(244, 67, 54, 0.5)', color: 'rgba(244, 67, 54, 0.9)', opacity: 0.7 }}>
                        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </div>
                    );
                  }

                  // Badges
                  const badges = [
                    { text: status, className: `explore-grid-card-badge--${status}` },
                    { text: contentType === 'course' ? 'Course' : 'Cookbook', className: 'explore-grid-card-badge--type' }
                  ];

                  // Actions
                  const actions = [];
                  if (status === 'deleted') {
                    actions.push({
                      icon: (
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                          <path d="M21 3v5h-5" />
                          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                          <path d="M3 21v-5h5" />
                        </svg>
                      ),
                      onClick: () => handleRestorePurchase(purchase.id, metadata.title),
                      className: 'explore-grid-card-action-icon--restore',
                      ariaLabel: 'Restore purchase',
                      title: 'Restore purchase',
                      disabled: restoringPurchaseId === purchase.id,
                      loading: restoringPurchaseId === purchase.id
                    });
                  }
                  actions.push(
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ),
                      onClick: () => handleViewDetails && handleViewDetails({ ...metadata, type: contentType, isPurchase: true, purchaseId: purchase.id }),
                      className: 'explore-grid-card-action-icon--preview',
                      ariaLabel: 'View details',
                      title: 'View details'
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      ),
                      onClick: () => handleExampleClick && handleExampleClick({ ...metadata, type: contentType, isPurchase: true, purchaseId: purchase.id }),
                      className: 'explore-grid-card-action-icon--details',
                      ariaLabel: 'Open',
                      title: 'Open'
                    }
                  );

                  return (
                    <ExploreCard
                      key={purchase.id}
                      title={metadata.title || 'Untitled'}
                      description={metadata.description || metadata.userPrompt || `Purchased ${contentType === 'course' ? 'course' : 'cookbook'} in ${purchaseLanguage}`}
                      language={purchaseLanguage}
                      category={metadata.category}
                      getCategoryDisplayName={getCategoryDisplayName}
                      icon="language"
                      iconSize="large"
                      contentType={contentType}
                      rightContent={rightContent}
                      badges={badges}
                      badgesBelowDescription={true}
                      actions={actions}
                      className={`explore-grid-card--owned ${status === 'deleted' ? 'explore-grid-card--deleted' : ''} ${status === 'restored' ? 'explore-grid-card--restored' : ''}`}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )
      }

      {/* Unified Course Listing Section - Hide when purchase history is shown */}
      {
        !showPurchaseHistory && (
          <div className="template-courses-wrapper">
            {/* Main Course Listing Section */}
            <div className="course-generator-main-listing-section">
              {/* Page Title and Subtitle */}
              <div className="course-generator-page-header">
                <h1 className="course-generator-page-title">
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="course-generator-page-title-icon">
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                  </svg>
                  Explore Courses & Cookbooks
                </h1>
                <p className="course-generator-page-subtitle">Discover Courses and Cookbooks to enhance your learning</p>
              </div>

              {/* Search Bar */}
              <div className="course-generator-header-nav-search">
                <div className="course-generator-search-bar">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" className="course-generator-search-icon">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    className="course-generator-search-input"
                    placeholder="Search by name or keyword"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="course-generator-search-clear"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div className="course-generator-main-listing-header">
                {/* Filter Bar with Chips (Preply Style) */}
                <div className="course-generator-filter-bar">
                  <div className="course-generator-filter-chips">
                    {/* Category Filter Button - First: shows categories in grid */}
                    <div className="course-generator-filter-chip course-generator-filter-chip--button">
                      <button
                        type="button"
                        className={`course-generator-filter-chip-button ${showCategoriesInGrid ? 'active' : ''} ${selectedCategory !== 'all' ? 'active' : ''}`}
                        onClick={() => {
                          setShowCategoriesInGrid(!showCategoriesInGrid);
                          if (!showCategoriesInGrid) {
                            setShowLanguagesInGrid(false);
                            setShowTypesInGrid(false);
                          }
                        }}
                        aria-label="Select category"
                      >
                        <span className="course-generator-filter-chip-label">Category:</span>
                        <span className="course-generator-filter-chip-value">
                          {selectedCategory === 'all' ? 'Any category' : getCategoryDisplayName(selectedCategory)}
                        </span>
                        <span className="dropdown-arrow">▼</span>
                      </button>
                    </div>

                    {/* Language Filter Chip */}
                    <div className="course-generator-filter-chip-wrapper">
                      <div className="course-generator-filter-chip course-generator-filter-chip--button">
                        <button
                          type="button"
                          className={`course-generator-filter-chip-button ${showLanguagesInGrid ? 'active' : ''}`}
                          onClick={() => {
                            setShowLanguagesInGrid(!showLanguagesInGrid);
                            if (!showLanguagesInGrid) {
                              setShowCategoriesInGrid(false);
                              setShowTypesInGrid(false);
                            }
                          }}
                          aria-label="Select language"
                        >
                          <span className="course-generator-filter-chip-value">
                            <IconRenderer
                              name={language.toLowerCase()}
                              type="language"
                              size="small"
                              className="course-generator-filter-chip-icon"
                            />
                            {language}
                          </span>
                          <span className="dropdown-arrow">▼</span>
                        </button>
                      </div>
                    </div>

                    {/* Type Filter Button - Shows types in grid */}
                    <div className="course-generator-filter-chip course-generator-filter-chip--button">
                      <button
                        type="button"
                        className={`course-generator-filter-chip-button ${showTypesInGrid ? 'active' : ''} ${exploreFilter !== 'all' ? 'active' : ''}`}
                        onClick={() => {
                          setShowTypesInGrid(!showTypesInGrid);
                          if (!showTypesInGrid) {
                            setShowLanguagesInGrid(false);
                            setShowCategoriesInGrid(false);
                          }
                        }}
                        aria-label="Select type"
                      >
                        <span className="course-generator-filter-chip-label">Type:</span>
                        <span className="course-generator-filter-chip-value">
                          {exploreFilter === 'courses' ? 'Courses' : exploreFilter === 'cookbooks' ? 'Cookbooks' : 'All'}
                        </span>
                        <span className="dropdown-arrow">▼</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Navigation Buttons - Right side */}
                <div className="course-generator-header-nav">
                  <button
                    type="button"
                    className={`course-generator__explore-btn ${!filterOwned ? 'active' : ''}`}
                    onClick={() => {
                      setFilterOwned(false);
                      scrollToExplore();
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                    <span>Explore </span>
                  </button>
                  <button
                    type="button"
                    className={`course-generator__explore-btn ${filterOwned ? 'active' : ''}`}
                    onClick={() => {
                      setFilterOwned(true);
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                    </svg>
                    <span>Owned</span>
                  </button>
                </div>
              </div>

              <div
                className={`template-courses-explore ${currentView === 'explore_info' ? 'modal-open' : ''}`}
                ref={exploreSectionRef}
              >
                {/* Show Categories in Grid or ExploreGrid, ExploreFront (carousel), or ExploreBackInfo based on currentView */}
                {currentView === 'explore_grid' ? (
                  <div
                    className="explore-grid-wrapper"
                    ref={exploreGridRef}
                    key={`${selectedCategory}-${language}`}
                  >
                    {showTypesInGrid ? (
                      <div className="explore-grid">
                        <ExploreCard
                          title="All"
                          description="Browse all courses and cookbooks"
                          icon="svg"
                          onClick={() => handleTypeSelect('all')}
                          className="category-card"
                          isActive={exploreFilter === 'all'}
                        >
                          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" className="explore-grid-card-icon">
                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                          </svg>
                        </ExploreCard>
                        <ExploreCard
                          title="Courses"
                          description="Explore structured learning courses"
                          icon="svg"
                          onClick={() => handleTypeSelect('courses')}
                          className="category-card"
                          isActive={exploreFilter === 'courses'}
                        >
                          <LearnModeIcon className="explore-grid-card-icon" style={{ width: '32px', height: '32px' }} />
                        </ExploreCard>
                        <ExploreCard
                          title="Cookbooks"
                          description="Browse reference cookbooks and guides"
                          icon="svg"
                          onClick={() => handleTypeSelect('cookbooks')}
                          className="category-card"
                          isActive={exploreFilter === 'cookbooks'}
                        >
                          <BookIcon className="explore-grid-card-icon" style={{ width: '32px', height: '32px' }} />
                        </ExploreCard>
                      </div>
                    ) : showLanguagesInGrid ? (
                      <div className="explore-grid">
                        {availableLanguages.length > 0 ? (
                          availableLanguages.map(lang => (
                            <ExploreCard
                              key={lang}
                              title={lang}
                              description={`Explore ${lang.toLowerCase()} courses and cookbooks`}
                              language={lang}
                              icon="language"
                              iconSize="large"
                              onClick={() => handleLanguageSelect(lang)}
                              className="category-card"
                              isActive={language === lang}
                            />
                          ))
                        ) : (
                          <div className="catalog-loading">
                            <div className="spinner"></div>
                            <p>Loading languages...</p>
                          </div>
                        )}
                      </div>
                    ) : showCategoriesInGrid ? (
                      <div className="explore-grid">
                        <ExploreCard
                          title="All Categories"
                          description="Browse all available courses and cookbooks across all categories"
                          icon="svg"
                          onClick={() => handleCategorySelect('all')}
                          className="category-card"
                          isActive={selectedCategory === 'all'}
                          badges={availableCategories.length > 0 ? [{
                            text: `${availableCategories.reduce((total, cat) => total + (cat.count || 0), 0)} ${availableCategories.reduce((total, cat) => total + (cat.count || 0), 0) === 1 ? 'item' : 'items'}`,
                            className: 'category-card-count'
                          }] : []}
                        >
                          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" className="explore-grid-card-icon">
                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                          </svg>
                        </ExploreCard>
                        {availableCategories.map(cat => (
                          <ExploreCard
                            key={cat.value}
                            title={cat.label}
                            description={`Explore ${cat.label.toLowerCase()} courses and cookbooks`}
                            language={language}
                            icon="language"
                            iconSize="small"
                            onClick={() => handleCategorySelect(cat.value)}
                            className="category-card"
                            isActive={selectedCategory === cat.value}
                            badges={cat.count !== undefined ? [{
                              text: `${cat.count} ${cat.count === 1 ? 'item' : 'items'}`,
                              className: 'category-card-count'
                            }] : []}
                          />
                        ))}
                      </div>
                    ) : isLoadingCatalog ? (
                      <div className="catalog-loading">
                        <div className="spinner"></div>
                        <p>Loading content catalog...</p>
                      </div>
                    ) : (
                      <ExploreGrid
                        templates={filteredTemplates}
                        language={language}
                        onCardClick={handleExampleClick}
                        onViewDetails={handleViewDetails}
                        isTemplateOwned={isTemplateOwned}
                        getCategoryDisplayName={getCategoryDisplayName}
                      />
                    )}
                  </div>
                ) : currentView === 'explore_front' ? (
                  <ExploreFront
                    templates={filteredTemplates}
                    currentIndex={currentCardIndex}
                    onCardClick={handleCardClick}
                    onNext={handleNextCard}
                    onPrevious={handlePreviousCard}
                    onShuffle={handleShuffle}
                    language={language}
                  />
                ) : null}
              </div>
            </div>
          </div>
        )
      }

      {/* Course Detail Modal Overlay - Rendered at root level for proper positioning */}
      {
        currentView === 'explore_info' && (
          <div className="course-detail-modal-overlay" onClick={handleBackToFront}>
            <div className="course-detail-modal-content" onClick={(e) => e.stopPropagation()}>
              <ExploreBackInfo
                mode={exploreInfoMode}
                template={selectedTemplate}
                language={language}
                onLanguageChange={setLanguage}
                onBack={handleBackToFront}
                onPreview={() => setCurrentView('explore_preview')}
                onBuy={handleBuyTemplate}
                onGenerate={handleGenerateFromForm}
                tokenBalance={tokenBalance}
                isGenerating={isGenerating}
                insufficientTokensError={insufficientTokensError}
                getTemplateOwnershipStatus={getTemplateOwnershipStatus}
                onRestore={handleRestorePurchase}
              />
            </div>
          </div>
        )
      }

      {/* Preview Modal Overlay */}
      {
        currentView === 'explore_preview' && (
          <div className="course-detail-modal-overlay" onClick={handleBackToFront}>
            <div className="course-detail-modal-content course-detail-modal-content--preview" onClick={(e) => e.stopPropagation()}>
              <ExplorePreview
                template={selectedTemplate}
                language={language}
                onBack={handleBackToFront}
                onViewDetails={() => setCurrentView('explore_info')}
                isTemplateOwned={isTemplateOwned}
              />
            </div>
          </div>
        )
      }

      {/* Course Generation Loading Screen */}
      <CourseGenerationLoading
        isVisible={isGenerating}
        progress={generationProgress}
        currentStep={currentGenerationStep}
      />
    </>
  );
};

export default CourseGenerator;
